import React, { useState, useEffect, useRef } from 'react';
import { Play, Loader2, CheckCircle2 } from 'lucide-react';
import StorageManager from '../services/StorageManager';
import { PULL_FACTORS } from '../utils/constants';
import VideoPlayer from './VideoPlayer';
import { StarIcon, Icon, AnimatedFlameIcon } from './Icons';
import LoginModal from '../pages/LoginModal';
import VoterNdaModal from './VoterNdaModal';

function VideoModal({ pilot, currentUser, onClose, onLogin, onNavigate, onWatchAnother, onPreviousPilot, onGoHome, onVoteSubmit, totalPilots, votedCount, showWelcome, pilotNumber }) {
  const videoRef = useRef(null);
  const modalRef = useRef(null);
  const [position, setPosition] = useState(() => ({
    x: Math.max(20, (window.innerWidth - 700) / 2), y: 40
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [videoEnded, setVideoEnded] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState(null);
  const [voteData, setVoteData] = useState({ curiosityScore: 0, seriesScore: 0, overallScore: 0, pullFactorsIn: [], pullFactorsBack: [] });
  const [existingVote, setExistingVote] = useState(null);
  const [loadingVote, setLoadingVote] = useState(true);
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pilotStats, setPilotStats] = useState(null);
  const [showVoteForm, setShowVoteForm] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [voteStep, setVoteStep] = useState(0); // 0=overall, 1=curiosity, 2=seriesPotential, 3=pullIn, 4=pullBack, 5=message, 6=confirm

  // NDA gate
  const [ndaAccepted, setNdaAccepted] = useState(() => {
    if (!currentUser) return false;
    return localStorage.getItem(`pilotlight_nda_${currentUser.id}`) === 'true';
  });

  // Commenting
  const [messageText, setMessageText] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [commentAnonymous, setCommentAnonymous] = useState(true);

  // Intro phase: welcome cards then pilot number
  // -1 = no intro, 0/1/2 = welcome cards, 3 = pilot number card, 4 = done
  const [introPhase, setIntroPhase] = useState(() => showWelcome ? 0 : 3);
  const [introFading, setIntroFading] = useState(false);
  const isFirstMount = useRef(true);

  const welcomeMessages = [
    'Thank you for your interest in this presentation.',
    'Please consider watching and voting on all pilots within the season.',
    'Your feedback is valued and appreciated.'
  ];

  // Advance intro phases automatically (only after NDA accepted)
  useEffect(() => {
    if (!ndaAccepted) return;
    if (introPhase < 0 || introPhase > 3) return;
    const showDuration = introPhase <= 2 ? 2200 : 1800;
    const totalDuration = introPhase <= 2 ? 2900 : 2400;
    const fadeTimer = setTimeout(() => setIntroFading(true), showDuration);
    const advanceTimer = setTimeout(() => {
      setIntroFading(false);
      setIntroPhase(prev => prev + 1);
    }, totalDuration);
    return () => { clearTimeout(fadeTimer); clearTimeout(advanceTimer); };
  }, [introPhase, ndaAccepted]);

  const displayPilot = pilot;
  const currentPilotIndex = votedCount || 0;

  // Accept NDA
  const handleAcceptNda = () => {
    localStorage.setItem(`pilotlight_nda_${currentUser.id}`, 'true');
    setNdaAccepted(true);
  };

  // Reset state when pilot changes
  useEffect(() => {
    setVoteData({ curiosityScore: 0, seriesScore: 0, overallScore: 0, pullFactorsIn: [], pullFactorsBack: [] });
    setExistingVote(null);
    setVoteSubmitted(false);
    setVideoEnded(false);
    setPilotStats(null);
    setShowVoteForm(false);
    setLoadingVote(true);
    setMessageText('');
    setMessageSent(false);
    setShowMessageForm(false);
    setCommentAnonymous(true);
    setVoteStep(0);
    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      setIntroPhase(3); // show "Pilot N" card for subsequent pilots
      setIntroFading(false);
    }

    const checkExistingVote = async () => {
      if (pilot?.id && currentUser?.id) {
        try {
          const vote = await StorageManager.getVoterVoteForPilot(currentUser.id, pilot.id);
          if (vote) {
            setExistingVote(vote);
            setVoteData({
              curiosityScore: vote.curiosityScore || 0,
              seriesScore: vote.seriesScore || 0,
              overallScore: vote.overallScore || 0,
              pullFactorsIn: vote.pullFactorsIn || [],
              pullFactorsBack: vote.pullFactorsBack || []
            });
            const stats = await StorageManager.getPilotStats(pilot.id);
            setPilotStats(stats);
            setShowVoteForm(false);
          }
        } catch (err) {
          console.error('Error checking existing vote:', err);
        }
      }
      setLoadingVote(false);
    };
    checkExistingVote();
  }, [pilot?.id, currentUser?.id]);

  // Initialize HLS video
  useEffect(() => {
    if (pilot?.playbackId && videoRef.current && currentUser && ndaAccepted) {
      const video = videoRef.current;
      const hlsUrl = `https://stream.mux.com/${pilot.playbackId}.m3u8`;
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = hlsUrl;
      } else if (window.Hls && Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
      }
    }
  }, [pilot?.playbackId, currentUser, ndaAccepted, introPhase]);

  // Dragging
  const handleHeaderMouseDown = (e) => {
    if (e.target.closest('button')) return;
    setIsDragging(true);
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  // Lock scroll
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y))
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleVideoEnd = () => setVideoEnded(true);

  // Pseudonymous vote — send voterId for dedup, strip name
  const handleVoteSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!currentUser) { setLoginModalMode('signup'); return; }
    setSubmitting(true);
    try {
      const votePayload = {
        pilotId: pilot.id, pilotTitle: pilot.pilotTitle, voterId: currentUser.id,
        voterLocation: '', voterGender: '',
        curiosityScore: voteData.curiosityScore, seriesScore: voteData.seriesScore, overallScore: voteData.overallScore,
        pullFactorsIn: voteData.pullFactorsIn, pullFactorsBack: voteData.pullFactorsBack
      };
      const result = await StorageManager.saveOrUpdateVote(votePayload);
      if (result.success) {
        setVoteSubmitted(true);
        if (onVoteSubmit) onVoteSubmit(pilot.id);
        const stats = await StorageManager.getPilotStats(pilot.id);
        setPilotStats(stats);

        // Send comment alongside vote if one was written
        if (messageText.trim() && displayPilot.creatorUserId) {
          try {
            await StorageManager.sendMessage({
              toCreatorUserId: displayPilot.creatorUserId,
              pilotId: displayPilot.id,
              pilotTitle: displayPilot.pilotTitle,
              messageText: messageText.trim(),
              senderName: commentAnonymous ? 'Anonymous' : (currentUser.displayName || currentUser.name || currentUser.email),
              fromUserId: currentUser.id
            });
            setMessageSent(true);
          } catch (err) { console.error('Comment send error:', err); }
        }
      } else { alert('Error submitting vote. Please try again.'); }
    } catch (err) { console.error('Vote error:', err); alert('Error submitting vote.'); }
    setSubmitting(false);
  };

  const togglePullFactor = (type, factor) => {
    const key = type === 'in' ? 'pullFactorsIn' : 'pullFactorsBack';
    setVoteData(prev => ({
      ...prev,
      [key]: prev[key].includes(factor) ? prev[key].filter(f => f !== factor) : [...prev[key], factor]
    }));
  };

  // Send message to creator
  const handleSendMessage = async () => {
    if (!messageText.trim() || !currentUser || !displayPilot.creatorUserId) return;
    setMessageSending(true);
    try {
      await StorageManager.sendMessage({
        toCreatorUserId: displayPilot.creatorUserId,
        pilotId: displayPilot.id,
        pilotTitle: displayPilot.pilotTitle,
        messageText: messageText.trim(),
        senderName: commentAnonymous ? 'Anonymous' : (currentUser.displayName || currentUser.name || currentUser.email),
        fromUserId: currentUser.id
      });
      setMessageSent(true);
      setMessageText('');
    } catch (err) { console.error('Comment error:', err); alert('Failed to send comment.'); }
    setMessageSending(false);
  };

  const ghostBtn = (id, disabled) => ({
    background: 'none', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '0.7rem 1.5rem',
    color: hoveredBtn === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.92rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.25s ease',
    letterSpacing: '0.06em',
    borderColor: hoveredBtn === id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
    opacity: disabled ? 0.5 : 1
  });

  if (!pilot) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes contentReveal { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      ` }} />

      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9998 }} />

      {/* Modal */}
      <div ref={modalRef} style={{
        position: 'fixed', left: position.x, top: position.y,
        width: 'min(700px, calc(100vw - 40px))', height: 'calc(100vh - 80px)',
        background: '#000000', borderRadius: '16px', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.7)', zIndex: 9999,
        border: '1px solid rgba(255,255,255,0.12)',
        display: 'flex', flexDirection: 'column', fontFamily: "'Cormorant Garamond', Georgia, serif"
      }}>

        {/* Header */}
        <div onMouseDown={handleHeaderMouseDown} style={{
          padding: '10px 14px', background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: isDragging ? 'grabbing' : 'grab', gap: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
            {/* Progress */}
            {totalPilots > 0 && (
              <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600,
                background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', flexShrink: 0, letterSpacing: '0.03em' }}>
                {pilotNumber || 1} / {totalPilots}
              </span>
            )}
            <h3 style={{ margin: 0, fontSize: 'clamp(0.8rem, 3vw, 0.95rem)', fontWeight: 500, color: 'rgba(255,255,255,0.8)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{displayPilot.pilotTitle}</h3>
          </div>
          <button onClick={onClose} title="Close"
            onMouseEnter={() => setHoveredBtn('close')} onMouseLeave={() => setHoveredBtn(null)}
            style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.12)',
              background: 'none', color: hoveredBtn === 'close' ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
              cursor: 'pointer', fontSize: '14px', flexShrink: 0, transition: 'all 0.2s' }}>×</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', overscrollBehavior: 'contain' }}>

          {/* NDA Gate — show before anything else */}
          {currentUser && !ndaAccepted && (
            <VoterNdaModal mandatory={false} onAccept={handleAcceptNda} onDecline={onClose} onClose={onClose} />
          )}

          {/* Intro sequence: welcome cards + pilot number (only after NDA accepted) */}
          {currentUser && ndaAccepted && introPhase >= 0 && introPhase <= 3 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: '300px', textAlign: 'center', padding: '2rem' }}>
              {introPhase <= 2 ? (
                <p key={introPhase} style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.2rem', fontWeight: 300,
                  color: 'rgba(255,255,255,0.6)', letterSpacing: '0.02em', lineHeight: 1.7,
                  maxWidth: '400px',
                  opacity: introFading ? 0 : 1, transform: introFading ? 'translateY(-8px)' : 'translateY(0)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                  animation: 'fadeInUp 0.6s ease-out'
                }}>{welcomeMessages[introPhase]}</p>
              ) : (
                <p style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.4rem', fontWeight: 300,
                  color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em',
                  opacity: introFading ? 0 : 1, transform: introFading ? 'translateY(-8px)' : 'translateY(0)',
                  transition: 'opacity 0.6s ease, transform 0.6s ease',
                  animation: 'fadeInUp 0.6s ease-out'
                }}>Pilot {pilotNumber || 1}</p>
              )}
            </div>
          )}

          {/* Video + Voting (only if NDA accepted and intro done) */}
          {introPhase > 3 && currentUser && ndaAccepted && (
            <div style={{ animation: 'contentReveal 1s ease-out' }}>
              {/* Video Player */}
              <div style={{ background: '#000', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
                <video ref={videoRef} controls onEnded={handleVideoEnd} key={displayPilot.id}
                  poster={displayPilot.playbackId ? `https://image.mux.com/${displayPilot.playbackId}/thumbnail.png` : undefined}
                  style={{ width: '100%', maxHeight: '55vh', display: 'block', objectFit: 'contain', background: '#000' }} />
              </div>

              {/* Logline */}
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: '6px', fontSize: '0.9rem' }}>{displayPilot.logline}</p>
              {displayPilot.creatorName && (
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', marginBottom: '16px' }}>by {displayPilot.creatorName}</p>
              )}

              {/* Loading vote check */}
              {loadingVote && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <AnimatedFlameIcon size={32} />
                </div>
              )}

              {/* Returning voter — already voted */}
              {!voteSubmitted && !loadingVote && existingVote && !showVoteForm && (
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '8px',
                    background: 'rgba(78,205,196,0.15)', color: '#4ecdc4' }}>Reviewed</span>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', margin: '10px 0 16px' }}>
                    You rated this pilot ★ {existingVote.overallScore}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {onPreviousPilot && pilotNumber > 1 && (
                      <button onClick={onPreviousPilot} style={{ ...ghostBtn('prev-reviewed', false), padding: '0.6rem 1.5rem' }}
                        onMouseEnter={() => setHoveredBtn('prev-reviewed')} onMouseLeave={() => setHoveredBtn(null)}>Previous Pilot</button>
                    )}
                    <button onClick={() => { setVoteStep(0); setShowVoteForm(true); }} style={{ ...ghostBtn('update', false), padding: '0.6rem 1.5rem' }}
                      onMouseEnter={() => setHoveredBtn('update')} onMouseLeave={() => setHoveredBtn(null)}>Update Vote</button>
                    {onWatchAnother && !allPilotsViewed && (
                      <button onClick={() => { const result = onWatchAnother(); if (result === 'all_viewed') setAllPilotsViewed(true); }}
                        style={{ ...ghostBtn('next-pulse', false), padding: '0.6rem 1.5rem' }}
                        onMouseEnter={() => setHoveredBtn('next-pulse')} onMouseLeave={() => setHoveredBtn(null)}>Next Pilot</button>
                    )}
                  </div>
                </div>
              )}

              {/* Step-by-step Vote Flow */}
              {!voteSubmitted && !loadingVote && (!existingVote || showVoteForm) && (
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255,255,255,0.08)', animation: 'fadeInUp 0.3s ease-out' }}>
                  {showVoteForm && existingVote && voteStep === 0 && (
                    <button onClick={() => setShowVoteForm(false)}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem',
                        cursor: 'pointer', marginBottom: '12px', fontFamily: "'Cormorant Garamond', Georgia, serif", padding: 0 }}>← Back</button>
                  )}

                  {/* Step indicator */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '18px' }}>
                    {[0,1,2,3,4,5,6].map(s => (
                      <div key={s} style={{ width: s === voteStep ? '20px' : '6px', height: '6px', borderRadius: '3px',
                        background: s < voteStep ? 'rgba(78,205,196,0.5)' : s === voteStep ? 'rgba(254,202,87,0.7)' : 'rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease' }} />
                    ))}
                  </div>

                  {/* Step 0: Overall Rating — optional, can skip */}
                  {voteStep === 0 && (
                    <div style={{ textAlign: 'center', animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em' }}>Overall Rating</label>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button" onClick={() => setVoteData(prev => ({...prev, overallScore: n}))}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', transition: 'transform 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                            <StarIcon size={36} filled={voteData.overallScore >= n} color="#feca57" emptyColor="rgba(255,255,255,0.15)" />
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setVoteStep(1)}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                          cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em', marginTop: '10px' }}
                        onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                        onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>
                        {voteData.overallScore > 0 ? 'next →' : 'skip'}
                      </button>
                    </div>
                  )}

                  {/* Step 1: Curiosity */}
                  {voteStep === 1 && (
                    <div style={{ textAlign: 'center', animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em' }}>Curiosity</label>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginBottom: '14px' }}>How curious are you to see more of this world?</p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '14px' }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button" onClick={() => setVoteData(prev => ({...prev, curiosityScore: n}))}
                            style={{ width: '36px', height: '36px', borderRadius: '50%',
                              border: voteData.curiosityScore >= n ? 'none' : '1px solid rgba(255,255,255,0.12)',
                              background: voteData.curiosityScore >= n ? 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)' : 'rgba(255,255,255,0.04)',
                              cursor: 'pointer', transition: 'all 0.2s' }} />
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '10px' }}>
                        <button onClick={() => setVoteStep(0)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>← back</button>
                        <button onClick={() => setVoteStep(2)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>{voteData.curiosityScore > 0 ? 'next →' : 'skip'}</button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Series Potential */}
                  {voteStep === 2 && (
                    <div style={{ textAlign: 'center', animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em' }}>Series Potential</label>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginBottom: '14px' }}>Would this make a great series?</p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '14px' }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button" onClick={() => setVoteData(prev => ({...prev, seriesScore: n}))}
                            style={{ width: '36px', height: '36px', borderRadius: '50%',
                              border: voteData.seriesScore >= n ? 'none' : '1px solid rgba(255,255,255,0.12)',
                              background: voteData.seriesScore >= n ? 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)' : 'rgba(255,255,255,0.04)',
                              cursor: 'pointer', transition: 'all 0.2s' }} />
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '10px' }}>
                        <button onClick={() => setVoteStep(1)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>← back</button>
                        <button onClick={() => setVoteStep(3)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>{voteData.seriesScore > 0 ? 'next →' : 'skip'}</button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Pull Factors In */}
                  {voteStep === 3 && (
                    <div style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em', textAlign: 'center' }}>What pulled you in?</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginBottom: '14px' }}>
                        {PULL_FACTORS.map(f => (
                          <button key={f} type="button" onClick={() => togglePullFactor('in', f)}
                            style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.78rem', fontFamily: "'Cormorant Garamond', Georgia, serif",
                              border: voteData.pullFactorsIn.includes(f) ? '1px solid rgba(78,205,196,0.5)' : '1px solid rgba(255,255,255,0.1)',
                              background: voteData.pullFactorsIn.includes(f) ? 'rgba(78,205,196,0.15)' : 'rgba(255,255,255,0.03)',
                              color: voteData.pullFactorsIn.includes(f) ? '#4ecdc4' : 'rgba(255,255,255,0.5)',
                              cursor: 'pointer', transition: 'all 0.2s' }}>{f}</button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button onClick={() => setVoteStep(2)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>← back</button>
                        <button onClick={() => setVoteStep(4)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>
                          {voteData.pullFactorsIn.length > 0 ? 'next →' : 'skip'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Pull Factors Back */}
                  {voteStep === 4 && (
                    <div style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em', textAlign: 'center' }}>What held it back?</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginBottom: '14px' }}>
                        {PULL_FACTORS.map(f => (
                          <button key={f} type="button" onClick={() => togglePullFactor('back', f)}
                            style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '0.78rem', fontFamily: "'Cormorant Garamond', Georgia, serif",
                              border: voteData.pullFactorsBack.includes(f) ? '1px solid rgba(255,107,107,0.5)' : '1px solid rgba(255,255,255,0.1)',
                              background: voteData.pullFactorsBack.includes(f) ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.03)',
                              color: voteData.pullFactorsBack.includes(f) ? '#ff6b6b' : 'rgba(255,255,255,0.5)',
                              cursor: 'pointer', transition: 'all 0.2s' }}>{f}</button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button onClick={() => setVoteStep(3)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>← back</button>
                        <button onClick={() => setVoteStep(5)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>
                          {voteData.pullFactorsBack.length > 0 ? 'next →' : 'skip'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 5: Leave a Comment */}
                  {voteStep === 5 && (
                    <div style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                      <label style={{ display: 'block', fontWeight: 400, marginBottom: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em', textAlign: 'center' }}>Leave a Comment</label>
                      <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)', margin: '0 0 12px', textAlign: 'center', lineHeight: 1.4 }}>
                        Send feedback directly to the creator.
                      </p>
                      <textarea
                        value={messageText} onChange={e => setMessageText(e.target.value)}
                        placeholder="Share your thoughts on this pilot..."
                        style={{ width: '100%', minHeight: '80px', padding: '0.8rem', background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff',
                          fontSize: '0.88rem', fontFamily: "'Cormorant Garamond', Georgia, serif", resize: 'vertical', outline: 'none',
                          boxSizing: 'border-box', marginBottom: '10px' }} />
                      {/* Anonymous toggle */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                        <button onClick={() => setCommentAnonymous(!commentAnonymous)}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '16px', padding: '4px 12px', cursor: 'pointer', transition: 'all 0.2s',
                            borderColor: !commentAnonymous ? 'rgba(78,205,196,0.4)' : 'rgba(255,255,255,0.1)' }}>
                          <div style={{ width: '28px', height: '16px', borderRadius: '8px', position: 'relative', transition: 'background 0.2s',
                            background: !commentAnonymous ? 'rgba(78,205,196,0.5)' : 'rgba(255,255,255,0.15)' }}>
                            <div style={{ position: 'absolute', width: '12px', height: '12px', borderRadius: '50%', background: '#fff',
                              top: '2px', left: !commentAnonymous ? '14px' : '2px', transition: 'left 0.2s' }} />
                          </div>
                          <span style={{ fontSize: '0.72rem', color: !commentAnonymous ? '#4ecdc4' : 'rgba(255,255,255,0.35)',
                            fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.03em', transition: 'color 0.2s' }}>
                            {commentAnonymous ? 'Anonymous' : `Signed as ${currentUser?.displayName || currentUser?.name || currentUser?.email}`}
                          </span>
                        </button>
                      </div>
                      {!commentAnonymous && (
                        <p style={{ fontSize: '0.68rem', color: 'rgba(255,107,107,0.5)', margin: '-6px 0 12px', textAlign: 'center', lineHeight: 1.3 }}>
                          Your name will be visible to the creator.
                        </p>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button onClick={() => setVoteStep(4)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>← back</button>
                        <button onClick={() => setVoteStep(6)}
                          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem',
                            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s', letterSpacing: '0.04em' }}
                          onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>
                          {messageText.trim() ? 'next →' : 'skip'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 6: Submit */}
                  {voteStep === 6 && (
                    <div style={{ textAlign: 'center', animation: 'fadeInUp 0.3s ease-out' }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: 400, marginBottom: '14px', color: 'rgba(255,255,255,0.6)',
                        fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: '0.05em' }}>
                        {existingVote ? 'Update Your Review' : 'Submit Review'}
                      </h4>
                      {/* Summary */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '16px' }}>
                        {voteData.overallScore > 0 && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(254,202,87,0.12)', border: '1px solid rgba(254,202,87,0.25)', color: '#feca57' }}>
                            ★ {voteData.overallScore}
                          </span>
                        )}
                        {voteData.curiosityScore > 0 && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', color: 'rgba(255,107,107,0.7)' }}>
                            Curiosity: {voteData.curiosityScore}
                          </span>
                        )}
                        {voteData.seriesScore > 0 && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(162,155,254,0.1)', border: '1px solid rgba(162,155,254,0.2)', color: 'rgba(162,155,254,0.7)' }}>
                            Series: {voteData.seriesScore}
                          </span>
                        )}
                        {voteData.pullFactorsIn.length > 0 && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(78,205,196,0.1)', border: '1px solid rgba(78,205,196,0.2)', color: 'rgba(78,205,196,0.7)' }}>
                            +{voteData.pullFactorsIn.length} pulled in
                          </span>
                        )}
                        {voteData.pullFactorsBack.length > 0 && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)', color: 'rgba(255,107,107,0.7)' }}>
                            +{voteData.pullFactorsBack.length} held back
                          </span>
                        )}
                        {messageText.trim() && (
                          <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem',
                            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)' }}>
                            + comment
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button onClick={() => setVoteStep(5)}
                          style={{ ...ghostBtn('back-6', false), padding: '0.6rem 1.5rem', fontSize: '0.78rem' }}
                          onMouseEnter={() => setHoveredBtn('back-6')} onMouseLeave={() => setHoveredBtn(null)}>← Back</button>
                        <button onClick={handleVoteSubmit} disabled={submitting}
                          style={{ ...ghostBtn('submit', submitting), padding: '0.6rem 2rem' }}
                          onMouseEnter={() => setHoveredBtn('submit')} onMouseLeave={() => setHoveredBtn(null)}>
                          {submitting ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Vote Submitted */}
              {voteSubmitted && (
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', padding: '20px', textAlign: 'center', animation: 'fadeInUp 0.4s ease-out' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Icon component={CheckCircle2} style={{ width: '28px', height: '28px', color: '#4ecdc4' }} />
                  </div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', margin: '0 0 4px', letterSpacing: '0.03em' }}>
                    {existingVote ? 'Vote Updated' : 'Vote Submitted'}
                  </h4>
                  <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', margin: '0 0 16px' }}>Your feedback has been recorded anonymously.</p>

                  {messageSent && (
                    <p style={{ color: '#4ecdc4', fontSize: '0.78rem', marginBottom: '16px' }}>Comment submitted.</p>
                  )}

                  {/* Navigation */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {onPreviousPilot && pilotNumber > 1 && (
                      <button onClick={onPreviousPilot}
                        style={{ ...ghostBtn('prev', false), flex: 1, padding: '0.8rem' }}
                        onMouseEnter={() => setHoveredBtn('prev')} onMouseLeave={() => setHoveredBtn(null)}>
                        Previous Pilot
                      </button>
                    )}
                    {onWatchAnother && (
                      <button onClick={() => onWatchAnother()}
                        style={{ ...ghostBtn('next', false), flex: 1, padding: '0.8rem' }}
                        onMouseEnter={() => setHoveredBtn('next')} onMouseLeave={() => setHoveredBtn(null)}>
                        {pilotNumber >= totalPilots ? 'Pick Top 3' : 'Next Pilot'}
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Not logged in */}
          {introPhase > 3 && !currentUser && (
            <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{
                width: '100%', aspectRatio: '16/9', minHeight: '160px',
                background: displayPilot.playbackId
                  ? `url(https://image.mux.com/${displayPilot.playbackId}/thumbnail.png) center/cover`
                  : '#111',
                filter: 'blur(15px) brightness(0.3)', transform: 'scale(1.1)'
              }} />
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', textAlign: 'center', padding: '1rem'
              }}>
                <Icon component={Play} style={{ width: '28px', height: '28px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }} />
                <h4 style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '4px', color: 'rgba(255,255,255,0.7)' }}>Sign in to Watch</h4>
                <button onClick={() => setLoginModalMode('login')}
                  style={ghostBtn('signin', false)}
                  onMouseEnter={() => setHoveredBtn('signin')} onMouseLeave={() => setHoveredBtn(null)}>Sign In</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Login Modal */}
      {loginModalMode && (
        <LoginModal
          onClose={() => setLoginModalMode(null)}
          onLogin={(user) => { onLogin(user); setLoginModalMode(null); }}
          onForgotPassword={onNavigate ? () => { setLoginModalMode(null); onClose(); onNavigate('forgot-password'); } : undefined}
          message="Sign in to watch and vote"
          initialMode={loginModalMode}
        />
      )}
    </>
  );
}

export default VideoModal;
